/**
 * InboxPage.tsx
 *
 * Full inbox list page, replacing the DashboardInboxModal.
 * Columns: Avatar | Sender | Type | Subject | Date | Actions
 * Tabs: Unread | All
 */
import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MailCheck, ExternalLink } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { DetailProfile } from '@/components/layout/DetailProfile';
import { DetailRightPanel } from '@/components/layout/DetailRightPanel';
import { usePatientDetail } from '@/hooks/usePatientDetail';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { Button } from '@/components/ui/Button';
import { usePatientNavigation } from '@/components/ui/PatientLink';
import { usePageContext } from '@/hooks/usePageContext';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import * as commSvc from '@/services/communications.service';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  INBOX_TABS,
  parseInboxTab,
  buildInboxHref,
  CHANNEL_STYLES,
  INBOX_TYPE_OPTIONS,
  channelIcon,
  relativeTime,
} from '@/pages/inbox/inboxShared';
import type { FilterConfig, FilterOption } from '@/types/filters.types';

// ─── Sidebar filter configs ─────────────────────────────────────────────────

const INBOX_FILTER_CONFIGS: FilterConfig[] = [
  { key: 'search', label: 'Search', type: 'search', allLabel: 'Search messages...' },
  { key: 'type', label: 'Type', type: 'select', allLabel: 'All types', multi: true },
];

const INBOX_FILTER_OPTIONS: Record<string, FilterOption[]> = {
  type: INBOX_TYPE_OPTIONS,
};

// ─── Component ──────────────────────────────────────────────────────────────

export default function InboxPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const navToPatient = usePatientNavigation();

  usePageContext('Inbox');

  // Tab
  const routeTab = parseInboxTab(searchParams.get('tab'));
  const [activeTab, setActiveTab] = useState(routeTab);
  const readStatus = activeTab === 'unread' ? 'unread' as const : 'all' as const;

  // Pagination + sorting
  const [page, setPage] = useState(1);
  const perPage = 25;
  const [sortKey, setSortKey] = useState<string>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const handleSortChange = useCallback((key: string, direction: 'asc' | 'desc') => {
    setSortKey(key);
    setSortDir(direction);
    setPage(1);
  }, []);

  // Patient quick-view side panel
  const [selectedPatient, setSelectedPatient] = useState<{ patientId: number; patientName: string } | null>(null);
  const [panelExpandTrigger, setPanelExpandTrigger] = useState(0);
  const { detail: patientDetail, loading: patientDetailLoading } = usePatientDetail(
    selectedPatient ? String(selectedPatient.patientId) : null,
  );

  const selectPatient = useCallback((patientId: number, patientName: string) => {
    if (!patientId) return;
    setSelectedPatient({ patientId, patientName });
    setPanelExpandTrigger((t) => t + 1);
  }, []);

  // Sidebar filters (search + type)
  const [filterValues, setFilterValues] = useState<Record<string, string[]>>({});
  const searchTerm = filterValues.search?.[0] ?? '';
  const selectedTypes = useMemo(() => filterValues.type ?? [], [filterValues.type]);
  // Stable primitive for the query key: a fresh [] each render would otherwise
  // change the key's identity and refetch on every render.
  const typeKey = selectedTypes.join(',');

  // ─── Data fetching ──────────────────────────────────────────────────────

  // isError matters as much as the rows here: without it a failed request and
  // a genuinely empty inbox both render "You have no unread messages.", which
  // is how a 500 on this endpoint got reported as a phantom unread count.
  const { data, isLoading, isError } = useQuery({
    queryKey: ['inbox-list', readStatus, page, sortKey, sortDir, searchTerm, typeKey],
    queryFn: () =>
      commSvc.getMyInbox({
        page,
        pageSize: perPage,
        readStatus,
        sortBy: sortKey,
        sortDir,
        search: searchTerm || undefined,
        types: selectedTypes,
      }),
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });

  const items = data?.data ?? [];
  const total = data?.meta?.total ?? 0;

  const { data: counts } = useQuery({
    queryKey: ['inbox-counts'],
    queryFn: () => commSvc.getInboxCounts(),
    staleTime: 30_000,
  });

  // ─── Selection + bulk mark-read ─────────────────────────────────────────

  const selectable = activeTab === 'unread';
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());

  useEffect(() => {
    setSelectedIds(new Set());
  }, [activeTab, page, searchTerm, typeKey]);

  const invalidateInboxQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['inbox-list'] });
    queryClient.invalidateQueries({ queryKey: ['inbox-counts'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard-my-inbox'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] });
    queryClient.invalidateQueries({ queryKey: ['navigation', 'badges'] });
  }, [queryClient]);

  const bulkMarkReadMutation = useMutation({
    mutationFn: (ids: number[]) => commSvc.bulkMarkInboxRead(ids, 'inbox'),
    onSuccess: () => {
      setSelectedIds(new Set());
      invalidateInboxQueries();
    },
  });

  const handleBulkMarkRead = useCallback(() => {
    const ids = Array.from(selectedIds).map((id) => Number(id)).filter((n) => Number.isFinite(n));
    if (ids.length === 0) return;
    bulkMarkReadMutation.mutate(ids);
  }, [selectedIds, bulkMarkReadMutation]);

  // ─── Row actions ────────────────────────────────────────────────────────

  const openPatientCommLog = useCallback(
    (entry: commSvc.CommLogEntry) => {
      if (Number(entry.wasRead ?? 0) === 0 && entry.commLogId) {
        void commSvc
          .markInboxEntryRead(entry.commLogId, 'inbox')
          .then(() => invalidateInboxQueries())
          .catch(() => {});
      }
      if (entry.patientId) {
        navigate(`/patients/${entry.patientId}?section=commlog&commLogId=${entry.commLogId}`);
        return;
      }
      navigate('/messages');
    },
    [navigate, invalidateInboxQueries],
  );

  const markReadOnly = useCallback(
    (entry: commSvc.CommLogEntry) => {
      if (!entry.commLogId) return;
      void commSvc
        .markInboxEntryRead(entry.commLogId, 'inbox')
        .then(() => invalidateInboxQueries())
        .catch(() => {});
    },
    [invalidateInboxQueries],
  );

  // ─── Columns ────────────────────────────────────────────────────────────

  const columns: DataTableColumn<commSvc.CommLogEntry>[] = useMemo(
    () => [
      {
        key: 'avatar',
        header: '',
        width: '48px',
        render: (row) => {
          const name =
            row.patientFirstName || row.patientLastName
              ? `${row.patientFirstName ?? ''} ${row.patientLastName ?? ''}`.trim()
              : 'Unknown';
          const initials = name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .slice(0, 2)
            .toUpperCase();
          const unread = Number(row.wasRead ?? 0) === 0;
          return (
            <div className="relative flex-shrink-0">
              <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-semibold text-slate-600">
                {initials}
              </div>
              {unread && (
                <span
                  aria-hidden="true"
                  className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-blue-500 ring-2 ring-white"
                />
              )}
            </div>
          );
        },
      },
      {
        key: 'patientName',
        header: 'Sender',
        sortable: true,
        render: (row) => {
          const name =
            row.patientFirstName || row.patientLastName
              ? `${row.patientFirstName ?? ''} ${row.patientLastName ?? ''}`.trim()
              : 'Unknown';
          const unread = Number(row.wasRead ?? 0) === 0;
          return (
            <button
              type="button"
              className={`text-left truncate hover:underline ${
                unread ? 'font-bold text-slate-950' : 'font-medium text-slate-600'
              }`}
              onClick={(e) => {
                e.stopPropagation();
                if (row.patientId) selectPatient(row.patientId, name);
              }}
            >
              {name}
            </button>
          );
        },
      },
      {
        key: 'type',
        header: 'Type',
        width: '90px',
        render: (row) => {
          const ch = CHANNEL_STYLES[row.type] ?? {
            label: row.type?.toUpperCase() ?? 'MSG',
            bg: 'bg-gray-100 text-gray-700',
          };
          return (
            <span
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${ch.bg}`}
            >
              {channelIcon(ch.icon)}
              {ch.label}
            </span>
          );
        },
      },
      {
        key: 'subject',
        header: 'Subject',
        sortable: true,
        truncate: true,
        render: (row) => {
          const unread = Number(row.wasRead ?? 0) === 0;
          return (
            <span className={unread ? 'font-semibold text-slate-700' : 'text-muted-foreground'}>
              {row.subject || '(No subject)'}
            </span>
          );
        },
      },
      {
        key: 'date',
        header: 'Date',
        sortable: true,
        width: '100px',
        render: (row) => {
          const unread = Number(row.wasRead ?? 0) === 0;
          return (
            <span
              className={`text-xs whitespace-nowrap ${
                unread ? 'font-semibold text-blue-600' : 'text-muted-foreground'
              }`}
            >
              {relativeTime(row.createdAt)}
            </span>
          );
        },
      },
      {
        key: 'actions',
        header: '',
        width: '48px',
        align: 'center',
        render: (row) => {
          const unread = Number(row.wasRead ?? 0) === 0;
          if (!unread) return null;
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="p-1 rounded hover:bg-blue-100 text-blue-500 hover:text-blue-700 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    markReadOnly(row);
                  }}
                >
                  <MailCheck className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Mark as read</TooltipContent>
            </Tooltip>
          );
        },
      },
    ],
    [selectPatient, markReadOnly],
  );

  // ─── Row styling ────────────────────────────────────────────────────────

  const rowClassName = useCallback((row: commSvc.CommLogEntry) => {
    const unread = Number(row.wasRead ?? 0) === 0;
    return unread ? 'bg-blue-50/30' : undefined;
  }, []);

  // ─── Tab counts ─────────────────────────────────────────────────────────

  const tabCounts = counts ?? { unread: 0, all: 0 };

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <ListPageLayout
      filterSidebar={
        <FilterSidebar
          filters={INBOX_FILTER_CONFIGS}
          values={filterValues}
          options={INBOX_FILTER_OPTIONS}
          negations={{}}
          onChange={(key, val) => {
            // INBOX_FILTER_CONFIGS declares only `search` and `select` filters,
            // and FilterControls emits a string[] for both; the daterange/range
            // members of FilterValue cannot reach this page.
            if (!Array.isArray(val)) return;
            setFilterValues((prev) => ({ ...prev, [key]: val }));
            setPage(1);
          }}
          onNegateChange={() => {}}
          onClear={(key) => {
            setFilterValues((prev) => {
              const next = { ...prev };
              delete next[key];
              return next;
            });
            setPage(1);
          }}
          searchCommitMode="blur-or-enter"
          showAIAssistant={false}
          storageKey="inbox"
        />
      }
      rightPanel={
        selectedPatient ? (
          <DetailRightPanel expandTrigger={panelExpandTrigger}>
            <div className="flex h-full min-h-0 w-full min-w-0 flex-col bg-white">
              <div className="flex items-center justify-end gap-1 border-b border-slate-200 px-3 py-1.5">
                <Button
                  variant="ghost"
                  size="sm"
                  className="shrink-0"
                  onClick={() =>
                    navToPatient({
                      patientId: selectedPatient.patientId,
                      name: selectedPatient.patientName,
                    })
                  }
                >
                  <ExternalLink className="mr-1 h-3.5 w-3.5" />
                  Open
                </Button>
              </div>
              <div className="min-h-0 flex-1">
                <DetailProfile
                  detail={patientDetail}
                  loading={patientDetailLoading}
                  onSectionNavigate={(section) =>
                    navigate(`/patients/${selectedPatient.patientId}?section=${section}`)
                  }
                />
              </div>
            </div>
          </DetailRightPanel>
        ) : undefined
      }
    >
      <div className="flex h-full min-h-0 flex-col">
        <div className="shrink-0 space-y-4 p-6 pb-0">
          <div className="flex items-center justify-between">
            <PageHeader
              variant="compact"
              title={
                (INBOX_TABS.find((t) => t.value === activeTab) ?? INBOX_TABS[0]).headerTitle
              }
            />
            {selectable && selectedIds.size > 0 && (
              <Button
                variant="primary"
                size="sm"
                onClick={handleBulkMarkRead}
                disabled={bulkMarkReadMutation.isPending}
              >
                {bulkMarkReadMutation.isPending
                  ? 'Marking...'
                  : `Mark ${selectedIds.size} Read`}
              </Button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1 border-b border-slate-200">
            {INBOX_TABS.map((tab) => {
              const isActive = activeTab === tab.value;
              const count = tabCounts[tab.countKey] ?? 0;
              return (
                <button
                  key={tab.value}
                  type="button"
                  title={tab.tooltip}
                  onClick={() => {
                    setActiveTab(tab.value);
                    setPage(1);
                    navigate(buildInboxHref(tab.value), { replace: true });
                  }}
                  className={`inline-flex items-center gap-1.5 rounded-t-md px-3 py-1.5 text-xs font-medium transition-colors ${
                    isActive
                      ? 'border-b-2 border-primary text-primary bg-primary/10'
                      : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {tab.label}
                  {tab.showBadge && count > 0 ? (
                    <span
                      className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-bold leading-none ${
                        isActive ? 'bg-primary text-white' : 'bg-red-500 text-white'
                      }`}
                    >
                      {count}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto">
          <DataTable<commSvc.CommLogEntry>
            data={items}
            columns={columns}
            keyField="commLogId"
            loading={isLoading}
            manualSort
            sortKey={sortKey}
            sortDir={sortDir}
            onSortChange={handleSortChange}
            page={page}
            pageSize={perPage}
            total={total}
            onPageChange={setPage}
            onRowClick={openPatientCommLog}
            rowClassName={rowClassName}
            selectable={selectable}
            selectedIds={selectable ? selectedIds : undefined}
            onSelectionChange={selectable ? setSelectedIds : undefined}
            emptyTitle={isError ? "Messages couldn't be loaded" : 'No messages'}
            emptyDescription={
              isError
                ? 'Refresh the page. If this keeps happening, contact your system administrator.'
                : activeTab === 'unread'
                  ? 'You have no unread messages.'
                  : 'No messages in your inbox.'
            }
          />
        </div>
      </div>
    </ListPageLayout>
  );
}
